if (!customElements.get('kivo-home-product-05')) {
  customElements.define(
    'kivo-home-product-05',
    class KivoHomeProduct05 extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.tabs = Array.from(this.querySelectorAll('[data-kivo-home-product-05-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-kivo-home-product-05-panel]'));
        this.rails = Array.from(this.querySelectorAll('[data-kivo-home-product-05-rail]'));
        this.scrollFrames = new Map();

        if (this.tabs.length === 0 || this.panels.length === 0) return;

        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleResize = this.handleResize.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        this.addEventListener('shopify:block:select', this.handleBlockSelect);
        this.rails.forEach((rail) => rail.addEventListener('scroll', this.handleScroll, { passive: true }));
        window.addEventListener('resize', this.handleResize, { passive: true });

        const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        // Initialize the selected group without pulling this off-screen section into the viewport.
        this.selectGroup(selectedTab.getAttribute('data-kivo-home-product-05-tab'), false, false);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.rails?.forEach((rail) => rail.removeEventListener('scroll', this.handleScroll));
        window.removeEventListener('resize', this.handleResize);
        this.scrollFrames?.forEach((frame) => window.cancelAnimationFrame(frame));
        this.scrollFrames?.clear();
      }

      handleClick(event) {
        const tab = event.target.closest('[data-kivo-home-product-05-tab]');
        if (tab) {
          this.selectGroup(tab.getAttribute('data-kivo-home-product-05-tab'), false);
          return;
        }

        const previousButton = event.target.closest('[data-kivo-home-product-05-prev]');
        if (previousButton) {
          this.move(previousButton.closest('[data-kivo-home-product-05-panel]'), -1);
          return;
        }

        const nextButton = event.target.closest('[data-kivo-home-product-05-next]');
        if (nextButton) this.move(nextButton.closest('[data-kivo-home-product-05-panel]'), 1);
      }

      handleKeydown(event) {
        const tab = event.target.closest('[data-kivo-home-product-05-tab]');
        if (!tab) return;

        const handledKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!handledKeys.includes(event.key)) return;

        event.preventDefault();
        const currentIndex = this.tabs.indexOf(tab);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowLeft') nextIndex = currentIndex === 0 ? this.tabs.length - 1 : currentIndex - 1;
        if (event.key === 'ArrowRight') nextIndex = currentIndex === this.tabs.length - 1 ? 0 : currentIndex + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;

        const nextTab = this.tabs[nextIndex];
        this.selectGroup(nextTab.getAttribute('data-kivo-home-product-05-tab'), true);
      }

      handleScroll(event) {
        const rail = event.currentTarget;
        const existingFrame = this.scrollFrames.get(rail);
        if (existingFrame) window.cancelAnimationFrame(existingFrame);

        const frame = window.requestAnimationFrame(() => {
          const panel = rail.closest('[data-kivo-home-product-05-panel]');
          const cards = this.cardsFor(panel);
          const step = this.cardStep(cards);
          const nextIndex = step > 0 ? Math.round(rail.scrollLeft / step) : 0;
          this.updatePanel(panel, nextIndex);
          this.scrollFrames.delete(rail);
        });

        this.scrollFrames.set(rail, frame);
      }

      handleBlockSelect(event) {
        const tabBlock = event.target.closest('[data-kivo-home-product-05-tab-block]');
        if (tabBlock) {
          this.selectGroup(tabBlock.getAttribute('data-kivo-home-product-05-tab-block'), false);
          return;
        }

        const card = event.target.closest('[data-kivo-home-product-05-card]');
        if (!card) return;

        const panel = card.closest('[data-kivo-home-product-05-panel]');
        const cards = this.cardsFor(panel);
        const index = cards.indexOf(card);
        this.selectGroup(panel.getAttribute('data-kivo-home-product-05-panel'), false);
        if (index >= 0) this.scrollToCard(panel, index, true);
      }

      handleResize() {
        if (!this.activePanel) return;
        const index = Number.parseInt(this.activePanel.dataset.currentIndex || '0', 10);
        this.scrollToCard(this.activePanel, index, true);
      }

      selectGroup(group, focusTab, scrollTab = true) {
        if (!group) return;

        const activePanel = this.panels.find(
          (panel) => panel.getAttribute('data-kivo-home-product-05-panel') === group
        );
        const activeTab = this.tabs.find((tab) => tab.getAttribute('data-kivo-home-product-05-tab') === group);
        if (!activePanel || !activeTab) return;

        this.loadPanel(activePanel);

        this.tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
          tab.closest('.kivo-home-product-05__label-item')?.classList.toggle('is-active', isActive);
        });

        this.panels.forEach((panel) => {
          const isActive = panel === activePanel;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
        });

        this.activePanel = activePanel;
        const currentIndex = Number.parseInt(activePanel.dataset.currentIndex || '0', 10);
        this.updatePanel(activePanel, currentIndex);

        if (focusTab) activeTab.focus();
        if (scrollTab) activeTab.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      }

      loadPanel(panel) {
        const sources = panel.querySelectorAll('[data-kivo-home-product-05-source]');
        const images = panel.querySelectorAll('[data-kivo-home-product-05-image]');

        sources.forEach((source) => {
          const srcset = source.getAttribute('data-kivo-home-product-05-srcset');
          if (!srcset) return;

          source.setAttribute('srcset', srcset);
          source.removeAttribute('data-kivo-home-product-05-srcset');
        });

        images.forEach((image) => {
          const srcset = image.getAttribute('data-kivo-home-product-05-image-srcset');
          const src = image.getAttribute('data-kivo-home-product-05-src');

          if (srcset) {
            image.setAttribute('srcset', srcset);
            image.removeAttribute('data-kivo-home-product-05-image-srcset');
          }

          if (src) {
            image.setAttribute('src', src);
            image.removeAttribute('data-kivo-home-product-05-src');
          }
        });

        panel.setAttribute('data-media-loaded', 'true');
      }

      cardsFor(panel) {
        return panel ? Array.from(panel.querySelectorAll('[data-kivo-home-product-05-card]')) : [];
      }

      cardStep(cards) {
        if (cards.length < 2) return cards[0]?.offsetWidth || 0;
        return cards[1].offsetLeft - cards[0].offsetLeft;
      }

      move(panel, direction) {
        const cards = this.cardsFor(panel);
        if (cards.length < 2) return;

        const currentIndex = Number.parseInt(panel.dataset.currentIndex || '0', 10);
        const nextIndex = (currentIndex + direction + cards.length) % cards.length;
        this.scrollToCard(panel, nextIndex);
      }

      scrollToCard(panel, index, instant = false) {
        const rail = panel?.querySelector('[data-kivo-home-product-05-rail]');
        const cards = this.cardsFor(panel);
        if (!rail || cards.length === 0) return;

        const safeIndex = Math.max(0, Math.min(index, cards.length - 1));
        const firstOffset = cards[0].offsetLeft;
        const targetOffset = cards[safeIndex].offsetLeft - firstOffset;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        rail.scrollTo({
          left: targetOffset,
          behavior: instant || reducedMotion ? 'auto' : 'smooth',
        });
        this.updatePanel(panel, safeIndex);
      }

      updatePanel(panel, index) {
        const cards = this.cardsFor(panel);
        if (!panel || cards.length === 0) return;

        const currentIndex = Math.max(0, Math.min(index, cards.length - 1));
        const progress = cards.length === 1 ? 100 : 30.19 + (currentIndex / (cards.length - 1)) * 69.81;
        const progressElement = panel.querySelector('[data-kivo-home-product-05-progress]');
        const status = panel.querySelector('[data-kivo-home-product-05-status]');

        panel.dataset.currentIndex = String(currentIndex);
        panel.style.setProperty('--kivo-home-product-05-progress', `${progress}%`);
        cards.forEach((card, cardIndex) => card.classList.toggle('is-current', cardIndex === currentIndex));

        if (status) status.textContent = `${currentIndex + 1} / ${cards.length}`;
        if (progressElement) progressElement.setAttribute('aria-valuenow', String(currentIndex + 1));
      }
    }
  );
}
